import { Module } from 'vuex'
import type { Event, EventPayload } from '@/ts-definitions'
import type { Command } from '../plugins/autoSave/types'
import { RootStore } from '../rootStore'

export type AutoSaveStore = {
	numberOfChanges: number
	numberOfReconnects: number
	isSending: boolean
	isPublishHotspotVisible: boolean
	queue: Array<Command>
	tempQueue: Array<Command>
}

type AutoSaveNamespace = 'autoSave'

type IncreaseChanges = Event<`${AutoSaveNamespace}/increaseChanges`>
type EnqueueCommand = EventPayload<
	`${AutoSaveNamespace}/enqueueCommand`,
	Command
>
type SetIsSending = EventPayload<`${AutoSaveNamespace}/setIsSending`, boolean>
type SetIsPublishHotspotVisible = EventPayload<
	`${AutoSaveNamespace}/setIsPublishHotspotVisible`,
	boolean
>
type OverrideFirstCommand = EventPayload<
	`${AutoSaveNamespace}/overrideFirstCommand`,
	Command
>
type SendMultiCommands = EventPayload<
	`${AutoSaveNamespace}/sendMultiCommands`,
	Command[]
>
type DeleteFirstCommand = Event<`${AutoSaveNamespace}/deleteFirstCommand`>
type CleanQueue = Event<`${AutoSaveNamespace}/cleanQueue`>
type Reconnect = Event<`${AutoSaveNamespace}/reconnect`>
type AskServerIfHasConnection = EventPayload<
	`${AutoSaveNamespace}/askServerIfHasConnection`,
	{ maxRetries: number }
>

export type AutoSaveMutations =
	| IncreaseChanges
	| EnqueueCommand
	| CleanQueue
	| DeleteFirstCommand
	| OverrideFirstCommand
	| Reconnect
	| SendMultiCommands
	| SetIsSending
	| SetIsPublishHotspotVisible
	| AskServerIfHasConnection

export const autoSaveStore: Module<AutoSaveStore, RootStore> = {
	namespaced: true,
	state: () => ({
		numberOfChanges: 0,
		numberOfReconnects: 0,
		isSending: false,
		isPublishHotspotVisible: false,
		queue: [],
		tempQueue: [],
	}),
	actions: {
		queueShift(context) {
			const firstCommand = context.state.queue[0]
			context.commit('deleteFirstCommand')
			return firstCommand
		},
	},
	mutations: {
		increaseChanges(state) {
			state.numberOfChanges++
		},
		enqueueCommand(state, { payload: command }: EnqueueCommand) {
			state.tempQueue.push(command)
		},
		reconnect(state) {
			state.numberOfReconnects++
		},
		deleteFirstCommand(state) {
			state.queue.shift()
		},
		cleanQueue(state) {
			state.queue = []
		},
		setIsSending(state, { payload }: SetIsSending) {
			state.isSending = payload
		},
		setIsPublishHotspotVisible(state, { payload }: SetIsPublishHotspotVisible) {
			state.isPublishHotspotVisible = payload
		},
		sendMultiCommands() {
			// SendMultiCommands
		},
		askServerIfHasConnection() {
			// This mutation is used to trigger the autoSave plugin
			// It doesn't do anything by itself
		},
	},
}
